import React from 'react';
import { Link } from 'react-router-dom';

/** English privacy policy — App Store / ASC App Privacy aligned. */
export default function PrivacyEn() {
  return (
    <>
      <h1>Privacy Policy</h1>
      <p className="muted">Effective date: May 27, 2026 · Last updated: May 27, 2026</p>
      <p className="lede">
        <strong>omw</strong> (&quot;在路上了&quot;) is a location-aware social app for discovering events, routes, treasures,
        and community on the map. This policy explains what personal information we collect, why we collect it, who we share it
        with, and the choices you have. It is written to align with Apple App Store Review Guideline 5.1 and the App Privacy
        details you configure in App Store Connect.
      </p>

      <h2>1. Who we are</h2>
      <p>
        omw is built and operated by an <strong>independent solo developer</strong>. &quot;omw&quot;, &quot;we&quot;, &quot;us&quot;,
        and &quot;our&quot; refer to that developer, who acts as the <strong>data controller</strong> for the personal information
        described below. Our public website is <a href="https://onmyway.city">onmyway.city</a>.
      </p>
      <p>
        Privacy contact: <a href="mailto:privacy@onmyway.city">privacy@onmyway.city</a>
        <br />
        Support: <a href="mailto:support@onmyway.city">support@onmyway.city</a>
        <br />
        Safety / abuse: <a href="mailto:safety@onmyway.city">safety@onmyway.city</a>
      </p>

      <h2>2. Plain-English summary</h2>
      <ul>
        <li>We do <strong>not</strong> sell your personal information, run third-party advertising, or use the IDFA for cross-app tracking.</li>
        <li>
          We collect what is needed to run a social, map-based product: account identifiers, profile information, content you
          post, location when you use map or outdoor features, usage signals, and diagnostics.
        </li>
        <li>
          Optional features — precise location, background location during navigation or recording, push notifications, voice
          interaction with Cheese (our in-app assistant), and Premium — are gated by OS permissions or in-app controls.
        </li>
        <li>
          You can <strong>delete your account in the app</strong> (Profile → Settings → Account → Delete account) or email{' '}
          <a href="mailto:privacy@onmyway.city">privacy@onmyway.city</a>.
        </li>
        <li>Sign in with Apple lets you hide your real email; we only receive what Apple chooses to share.</li>
      </ul>

      <h2>3. Scope</h2>
      <p>
        This policy applies to the omw mobile app (iOS and Android), our APIs, and the onmyway.city website. It does not cover
        third-party services you access through omw (for example Google Maps data layers or a merchant&apos;s own website); those
        services have their own policies.
      </p>

      <h2>4. Information we collect</h2>
      <p>
        The table below mirrors Apple&apos;s App Privacy categories. &quot;Linked to you&quot; means associated with your account
        or device identifier. We do <strong>not</strong> use collected data to track you across other companies&apos; apps or
        websites for advertising.
      </p>
      <table>
        <thead>
          <tr>
            <th>Apple category</th>
            <th>What we collect</th>
            <th>Linked to you?</th>
            <th>Used for tracking?</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Contact info</td>
            <td>
              Email address (email/password sign-up, Google Sign-In, or Sign in with Apple). Phone number only when a verified
              merchant completes business verification (SMS OTP).
            </td>
            <td>Yes</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Identifiers</td>
            <td>
              omw user ID, Firebase Authentication UID, Apple/Google subject identifier, Expo push token, device push token.
            </td>
            <td>Yes</td>
            <td>No</td>
          </tr>
          <tr>
            <td>User content</td>
            <td>
              Display name, avatar, bio, interests, posts, comments, event listings, treasure capsules and photos, recorded
              routes/tracks, script-game content, chat messages, merchant storefront details, coupon descriptions, SOS messages
              you broadcast, and digital legacy (&quot;will&quot;) materials you choose to store.
            </td>
            <td>Yes</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Location</td>
            <td>
              <strong>Coarse and precise location</strong> when you grant permission: map centre, nearby events/treasures,
              weather for your area, route navigation, track recording, live event check-ins, multiplayer games, Cheese proximity
              alerts (merchant coupons and nearby SOS), and city-level leaderboards. Background location may run during active
              navigation or track recording if you enable it in iOS settings.
            </td>
            <td>Yes</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Usage data</td>
            <td>
              Product interactions (screens opened, features used), in-app actions (events joined, routes viewed, coupons
              collected), language and theme preferences, and <strong>behavior events</strong> used to personalize recommendations
              and city leaderboards (for example app opens, event views, route navigation — aggregated into charisma/engagement
              scores).
            </td>
            <td>Yes</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Diagnostics</td>
            <td>Crash data, performance traces, error logs, app version, OS version, device model.</td>
            <td>Yes</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Sensitive info</td>
            <td>
              Optional self-reported attributes you choose to provide (for example age, gender, dating intent, friend
              preferences). You may leave these blank or remove them in Settings.
            </td>
            <td>Yes</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Purchases</td>
            <td>
              Apple In-App Purchase receipt identifiers, product IDs, subscription status, and TestFlight promotional Premium
              grants. We never receive your payment card details — Apple processes payments.
            </td>
            <td>Yes</td>
            <td>No</td>
          </tr>
        </tbody>
      </table>
      <p>
        <strong>What we do not collect:</strong> address-book contacts, your full photo library (only images you explicitly
        pick), browsing history outside omw, health or clinical records, government ID numbers, or continuous audio recordings.
        Voice input for Cheese and SOS confirmation is processed on-device through Apple&apos;s speech APIs; we do not store
        raw audio files on our servers.
      </p>
      <p>
        <strong>Retired features:</strong> Phone-number login and SMS password reset are not offered to general users. SMS is
        used only for optional merchant phone verification.
      </p>

      <h2>5. How we use your information</h2>
      <ul>
        <li><strong>Provide the service:</strong> authenticate you, render maps, show nearby content, deliver chat, run events, scripts, routes, leaderboards, and merchant tools.</li>
        <li><strong>Personalize your experience:</strong> Cheese assistant replies, city briefings, event recommendations, and proactive suggestions based on behavior events and city context.</li>
        <li><strong>Keep users safe:</strong> rate limits, fraud/abuse detection, blocking, reporting, content moderation, and SOS visibility to nearby users who opt in.</li>
        <li><strong>Improve the product:</strong> aggregate, de-identified analytics about feature usage and reliability.</li>
        <li><strong>Communicate with you:</strong> push notifications (friend requests, chat, events, coupons — you can disable in OS settings), security notices, and support replies.</li>
        <li><strong>Legal compliance:</strong> respond to lawful requests, tax/accounting records, and enforce our <Link to="/terms">Terms of Service</Link>.</li>
      </ul>
      <p>We do not use your data for automated decisions that produce legal or similarly significant effects.</p>

      <h2>6. Legal bases (EEA, UK, Switzerland)</h2>
      <ul>
        <li><strong>Contract</strong> — to provide features you sign up for.</li>
        <li><strong>Legitimate interests</strong> — security, abuse prevention, product improvement (you may object).</li>
        <li><strong>Consent</strong> — precise/background location, push notifications, optional profile fields, and marketing if ever offered.</li>
        <li><strong>Legal obligation</strong> — when retention is required by law.</li>
      </ul>

      <h2>7. AI, voice, and assistant features</h2>
      <p>
        omw includes AI-assisted features similar in scope to discovery assistants on other social platforms, but focused on
        real-world activities:
      </p>
      <ul>
        <li>
          <strong>Cheese assistant</strong> (personal agent, community agent for verified merchants, city intel) — text or voice
          messages you send may be transmitted to our servers and to Google Gemini (or similar LLM providers under contract) to
          generate replies. We send the minimum context needed (your message, recent conversation, city code, and non-sensitive
          preferences). We do not attach your email or precise coordinates to these requests unless you include them in text.
        </li>
        <li>
          <strong>On-device speech</strong> — when you tap the microphone, Apple&apos;s speech recognition converts speech to text
          on your device. We receive the <strong>transcript</strong>, not a stored audio recording.
        </li>
        <li>
          <strong>Generated content</strong> — riddles, script scenes, and city summaries are generated for you; you choose
          whether to publish or share them. See our <Link to="/terms">Terms</Link> regarding your responsibility for published
          content.
        </li>
      </ul>
      <p>
        LLM providers process inputs only to return outputs to us and, under our agreements, do not use your content to train
        general-purpose models without separate consent.
      </p>

      <h2>8. Location data (important)</h2>
      <p>
        Like Geocaching, AllTrails, and other outdoor social apps, omw depends on location to function. We follow these
        principles:
      </p>
      <ul>
        <li><strong>Permission first:</strong> iOS/Android ask before precise location; you may deny and still browse some non-map features.</li>
        <li><strong>Purpose limitation:</strong> location is used only for the feature you are using (map, navigation, event, game, proximity alert).</li>
        <li><strong>Foreground by default:</strong> most features use location only while the app is open and active.</li>
        <li>
          <strong>Background location</strong> may be used during route navigation or GPS track recording if you start those
          features and grant &quot;Always&quot; / background permission. Background updates stop when you end the session or leave
          the feature.
        </li>
        <li>
          <strong>Cheese proximity (Premium):</strong> while the app is foregrounded, we may periodically check location (often
          using cached/last-known fixes) to suggest nearby merchant coupons after you dwell in one place, or to announce a nearby
          SOS broadcast. You can disable Premium or location permission to stop this.
        </li>
        <li><strong>Live sharing:</strong> when you join a live event, script session, or PvP game, other participants may see your position for the duration of that session.</li>
        <li><strong>No selling of location:</strong> we do not sell precise location data to data brokers or advertisers.</li>
      </ul>

      <h2>9. Leaderboards, charisma &amp; public activity</h2>
      <p>
        City leaderboards (host charisma, popular events, tags, and similar) are computed from <strong>aggregated activity</strong>{' '}
        such as events you organize, registrations, check-ins, saves, and engagement metrics over a rolling window (typically 90
        days). Your display name and avatar may appear on public leaderboards when you rank. You can limit some visibility via
        profile privacy settings; deleting your account removes you from future rankings.
      </p>

      <h2>10. What other users can see</h2>
      <p>By design, the following may be visible to other omw users:</p>
      <ul>
        <li>Display name, avatar, bio, and profile fields you mark public.</li>
        <li>Content you publish: treasures, events, routes, comments, and merchant offers.</li>
        <li>Your live position to participants in an event, game, or session you joined.</li>
        <li>Chat messages to conversation participants.</li>
        <li>Leaderboard placement and charisma score in your city when you rank.</li>
      </ul>
      <p>
        Your email, phone number, authentication identifiers, and exact device IDs are <strong>never</strong> shown to other
        users.
      </p>

      <h2>11. Service providers (subprocessors)</h2>
      <p>We use trusted vendors who process data only on our instructions:</p>
      <table>
        <thead>
          <tr>
            <th>Provider</th>
            <th>Purpose</th>
            <th>Typical data</th>
            <th>Region</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Google Cloud (Cloud Run, Cloud SQL, Cloud Storage, Firebase Auth)</td>
            <td>Hosting, database, files, authentication</td>
            <td>Account &amp; app data</td>
            <td>United States</td>
          </tr>
          <tr>
            <td>Google Gemini</td>
            <td>AI assistant &amp; content generation</td>
            <td>Text/images you submit to AI features</td>
            <td>United States</td>
          </tr>
          <tr>
            <td>Mapbox</td>
            <td>Maps &amp; geocoding</td>
            <td>Map viewport / coordinates</td>
            <td>United States</td>
          </tr>
          <tr>
            <td>Stream (GetStream.io)</td>
            <td>Chat infrastructure</td>
            <td>Display name, avatar URL, messages</td>
            <td>United States</td>
          </tr>
          <tr>
            <td>Apple (Sign in with Apple, APNs, on-device speech)</td>
            <td>Login, push delivery, speech-to-text</td>
            <td>Auth tokens, push token, transcripts</td>
            <td>Per Apple policy</td>
          </tr>
          <tr>
            <td>Google Sign-In</td>
            <td>OAuth login</td>
            <td>Email, name, avatar as permitted</td>
            <td>United States</td>
          </tr>
          <tr>
            <td>OpenWeather</td>
            <td>Weather display</td>
            <td>Coarse location</td>
            <td>United Kingdom</td>
          </tr>
          <tr>
            <td>Twilio / email SMTP (SendGrid, etc.)</td>
            <td>Merchant verification OTP</td>
            <td>Phone or email, OTP</td>
            <td>United States / varies</td>
          </tr>
        </tbody>
      </table>

      <h2>12. International transfers</h2>
      <p>
        Our primary infrastructure is in the United States. If you access omw from Australia, China, the EEA, or elsewhere, your
        data may be transferred internationally. Where required, we use Standard Contractual Clauses and comparable safeguards
        with processors.
      </p>

      <h2>13. Retention</h2>
      <ul>
        <li>Account data — while your account is active.</li>
        <li>User content — until you delete it or delete your account (some anonymized aggregates may remain in leaderboards until recalculated).</li>
        <li>Behavior events — typically up to 24 months, then aggregated or deleted.</li>
        <li>Diagnostic logs — up to 30 days.</li>
        <li>Database backups — up to 35 days.</li>
        <li>
          After account deletion — personal data is anonymized or erased within <strong>30 days</strong>, except minimal records
          we must keep for law (for example IAP tax logs), retained only as long as legally required.
        </li>
      </ul>

      <h2>14. Your choices &amp; rights</h2>
      <p>Depending on your location, you may have the right to access, correct, delete, export, restrict, or object to processing, and to withdraw consent.</p>
      <ul>
        <li><strong>In-app:</strong> Profile → Settings (privacy toggles, cache, logout, delete account).</li>
        <li><strong>OS settings:</strong> location, notifications, microphone, speech recognition.</li>
        <li><strong>Email:</strong> <a href="mailto:privacy@onmyway.city">privacy@onmyway.city</a> — we respond within 30 days.</li>
      </ul>

      <h2>15. Account deletion</h2>
      <p>
        You may delete your account at any time in the app: <strong>Profile → Settings → Account → Delete account</strong>. You
        must type <code>DELETE</code> to confirm; password accounts also require your password. Deletion anonymizes your profile,
        clears contact/OAuth identifiers so you can register again, deactivates push tokens, and signs you out. Content you
        created may remain in anonymized form where other users or legal obligations require (for example event attendance logs).
      </p>

      <h2>16. California (CCPA / CPRA)</h2>
      <p>
        We do not sell or share personal information for cross-context behavioural advertising. California residents may request
        access, deletion, and correction via <a href="mailto:privacy@onmyway.city">privacy@onmyway.city</a>.
      </p>

      <h2>17. Australia</h2>
      <p>
        If you are in Australia, you may contact us to access or correct personal information under the Privacy Act 1988 (Cth).
        If you are not satisfied with our response, you may contact the Office of the Australian Information Commissioner (OAIC).
      </p>

      <h2>18. China &amp; other regions</h2>
      <p>
        If you use omw from mainland China or other jurisdictions with local privacy laws (including PIPL), you may have
        additional rights. Contact <a href="mailto:privacy@onmyway.city">privacy@onmyway.city</a> and we will respond under applicable
        law. A Chinese-language version of this policy is available on this page — switch language in the site header.
      </p>

      <h2>19. Children</h2>
      <p>
        omw is not directed to children under <strong>13</strong> (or 16 in parts of the EEA, or 14 in China where applicable).
        We do not knowingly collect children&apos;s data. Contact us to request deletion if you believe a child registered.
      </p>

      <h2>20. Tracking &amp; App Tracking Transparency</h2>
      <p>
        We do not track you across third-party apps or websites for advertising and do not use the IDFA. We do not show Apple&apos;s
        App Tracking Transparency prompt for advertising purposes.
      </p>

      <h2>21. Security</h2>
      <p>
        We use HTTPS, hashed passwords (bcrypt), encrypted cloud storage, private database networking, and short-lived production
        credentials. No system is perfectly secure; if a breach affects your data, we will notify you and regulators as required.
      </p>

      <h2>22. Cookies &amp; local storage</h2>
      <p>
        The mobile app stores your auth token and preferences in secure on-device storage. The onmyway.city marketing site does not
        use advertising cookies or third-party analytics trackers. Language preference may be stored in browser local storage.
      </p>

      <h2>23. Changes</h2>
      <p>
        We may update this policy. Material changes will be noted in-app and on this page. The &quot;Last updated&quot; date always
        reflects the current version.
      </p>

      <h2>24. Contact</h2>
      <p>
        Privacy: <a href="mailto:privacy@onmyway.city">privacy@onmyway.city</a>
        <br />
        Support: <a href="mailto:support@onmyway.city">support@onmyway.city</a>
        <br />
        Safety: <a href="mailto:safety@onmyway.city">safety@onmyway.city</a>
      </p>
    </>
  );
}
